/**
 * @file carousel-grid.tsx
 * Expanded "Show all" view rendering carousel items as a responsive, vertically scrolling grid.
 * Supports both RSC mode (children) and CSR data-driven mode (items + renderItem).
 *
 * @see docs/m3/carousel/Carousel_Accessibility_Material_Design_3.md
 */

import * as React from "react";

export interface CarouselGridProps<T = unknown>
	extends React.HTMLAttributes<HTMLDivElement> {
	children?: React.ReactNode;
	items?: T[];
	renderItem?: (item: T, index: number) => React.ReactNode;
	preferredItemWidth?: number;
}

export function CarouselGrid<T = unknown>({
	children,
	items,
	renderItem,
	preferredItemWidth = 240,
	className,
	style,
	...props
}: CarouselGridProps<T>) {
	const gridStyle: React.CSSProperties = {
		display: "grid",
		gridTemplateColumns: `repeat(auto-fill, minmax(${preferredItemWidth}px, 1fr))`,
		gap: "8px",
		padding: "8px 16px",
		width: "100%",
		boxSizing: "border-box",
		...style,
	};

	const itemStyle: React.CSSProperties = {
		position: "relative",
		width: "100%",
		minHeight: "180px",
		borderRadius: "28px",
		overflow: "hidden",
	};

	// Render items from CSR items array if provided
	if (items && renderItem) {
		return (
			<section
				className={className}
				style={gridStyle}
				aria-label="Expanded carousel grid"
				{...props}
			>
				{items.map((item, index) => {
					const itemKey =
						typeof item === "object" && item !== null && "id" in item
							? (item as { id: React.Key }).id
							: typeof item === "object" && item !== null && "key" in item
								? (item as { key: React.Key }).key
								: index;

					return (
						<div key={itemKey} style={itemStyle}>
							{renderItem(item, index)}
						</div>
					);
				})}
			</section>
		);
	}

	// RSC Mode: Render direct children
	return (
		<section
			className={className}
			style={gridStyle}
			aria-label="Expanded carousel grid"
			{...props}
		>
			{React.Children.map(children, (child, index) => {
				const childKey =
					React.isValidElement(child) && child.key != null ? child.key : index;

				return (
					<div key={childKey} style={itemStyle}>
						{child}
					</div>
				);
			})}
		</section>
	);
}
